import { useQuery } from '@tanstack/react-query'
import { api } from '@api'
import type { Contact } from './types/contactTypes'
import type { PaginatedResponse } from './types/paginatedTypes'

// The server puts the whole list together for each request, whatever the page
// asked, and sets no ceiling on the size of a page: one large page costs it
// the work once, where pages of 500 cost it once per page.
const PAGE_SIZE = 5000
// A guard against a server that would never stop announcing more.
const MAX_PAGES = 2

const fetchClassifyContacts = async (): Promise<Contact[]> => {
  // Include ghost profiles (imported / hand-created contacts): they are exactly
  // the ones that still need to be sorted into Privé / Pro here.
  // Every page is loaded: the pile is the whole list, not its first 200.
  const contacts: Contact[] = []
  const seenIds = new Set<string>()
  let loaded = 0
  for (let page = 1; page <= MAX_PAGES; page++) {
    const { data } = await api.get<PaginatedResponse<Contact>>(
      `/contacts?limit=${PAGE_SIZE}&page=${page}`,
    )
    const rows = data.data ?? []
    if (rows.length === 0) break
    loaded += rows.length
    // Pages of a server that does not cut the list once can overlap.
    for (const contact of rows) {
      if (seenIds.has(contact.id)) continue
      seenIds.add(contact.id)
      contacts.push(contact)
    }
    if (loaded >= (data.metadata?.count ?? 0)) break
  }
  return contacts
}

export const useFtClassifyContacts = () => {
  return useQuery<Contact[]>({
    queryKey: ['ft-classify-contacts'],
    queryFn: fetchClassifyContacts,
  })
}
